"use client";

/**
 * The release/track artwork strip: every picture of the release in a row,
 * the first one is the cover. Admins reorder by HOLD + DRAG — a mouse drags
 * after a few pixels, a finger after a short hold (Gordon 2026-11-03: "I need
 * to drag move order of thumbnails of the release" — the old HTML5 drag only
 * worked with a mouse). The list reorders live under the finger; letting go
 * saves the new order. Alt + ←/→ moves the focused picture too.
 */

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { Loader2, Upload } from "lucide-react";
import { cn } from "@/lib/utils";

const HOLD_MS = 280;
const SLOP = 8;

function move<T>(list: T[], from: number, to: number): T[] {
  const next = [...list];
  const [it] = next.splice(from, 1);
  next.splice(to, 0, it);
  return next;
}

export function ArtworkStrip({
  images, canManage, uploading, onOpen, onRemove, onOrder, onAdd,
}: {
  images: string[];
  canManage: boolean;
  uploading?: boolean;
  onOpen: (index: number) => void;
  onRemove: (url: string) => void;
  /** The full new order; the first entry becomes the cover. */
  onOrder: (urls: string[]) => void;
  onAdd: () => void;
}) {
  const t = useTranslations("music.artStrip");
  const rowRef = useRef<HTMLDivElement>(null);
  // Live order while dragging (null = show `images` as they are).
  const [live, setLive] = useState<string[] | null>(null);
  const [dragUrl, setDragUrl] = useState<string | null>(null);
  // Remove asks here, in the page: the browser's own confirm() never shows in the
  // in-app browser or the Mac app, so "×" did nothing (Gordon 2026-11-03).
  const [confirmUrl, setConfirmUrl] = useState<string | null>(null);
  const tc = useTranslations("common");
  const press = useRef<{ url: string; x: number; y: number; timer: ReturnType<typeof setTimeout> | null; mouse: boolean } | null>(null);
  const liveRef = useRef<string[] | null>(null);
  const dragRef = useRef<string | null>(null);
  const suppressClick = useRef(false);
  const imagesRef = useRef(images);
  imagesRef.current = images;
  const onOrderRef = useRef(onOrder);
  onOrderRef.current = onOrder;

  const shown = live ?? images;

  useEffect(() => {
    if (!canManage) return;
    const row = rowRef.current;
    if (!row) return;

    const startDrag = (url: string) => {
      dragRef.current = url;
      liveRef.current = [...imagesRef.current];
      setDragUrl(url);
      setLive(liveRef.current);
      try { navigator.vibrate?.(10); } catch { /* not supported */ }
    };
    const over = (x: number, y: number) => {
      const url = dragRef.current;
      const list = liveRef.current;
      if (!url || !list) return;
      const el = document.elementFromPoint(x, y)?.closest<HTMLElement>("[data-art-idx]");
      // Scroll the strip when the finger nears its edges.
      const r = row.getBoundingClientRect();
      if (x < r.left + 32) row.scrollLeft -= 10;
      else if (x > r.right - 32) row.scrollLeft += 10;
      if (!el || !row.contains(el)) return;
      const to = Number(el.dataset.artIdx);
      const from = list.indexOf(url);
      if (Number.isFinite(to) && from >= 0 && to !== from) {
        liveRef.current = move(list, from, to);
        setLive(liveRef.current);
      }
    };
    const finish = () => {
      const p = press.current;
      if (p?.timer) clearTimeout(p.timer);
      press.current = null;
      const url = dragRef.current;
      const next = liveRef.current;
      dragRef.current = null;
      liveRef.current = null;
      setDragUrl(null);
      setLive(null);
      if (url) {
        suppressClick.current = true;
        setTimeout(() => { suppressClick.current = false; }, 50);
        const before = imagesRef.current;
        if (next && next.some((u, i) => u !== before[i])) onOrderRef.current(next);
      }
    };
    const urlAt = (target: EventTarget | null) =>
      (target as HTMLElement | null)?.closest<HTMLElement>("[data-art-url]")?.dataset.artUrl || null;

    // Touch: hold still, then drag. Moving first = a normal scroll.
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) { finish(); return; }
      const url = urlAt(e.target);
      if (!url || (e.target as HTMLElement).closest("[data-art-remove]")) return;
      const tch = e.touches[0];
      press.current = {
        url, x: tch.clientX, y: tch.clientY, mouse: false,
        timer: setTimeout(() => { if (press.current) startDrag(press.current.url); }, HOLD_MS),
      };
    };
    const onTouchMove = (e: TouchEvent) => {
      const p = press.current;
      if (!p) return;
      const tch = e.touches[0];
      if (dragRef.current) {
        e.preventDefault(); // no scrolling while a picture is held
        over(tch.clientX, tch.clientY);
      } else if (Math.hypot(tch.clientX - p.x, tch.clientY - p.y) > SLOP) {
        if (p.timer) clearTimeout(p.timer);
        press.current = null;
      }
    };
    // Mouse: drag after a few pixels.
    const onMouseMove = (e: PointerEvent) => {
      const p = press.current;
      if (!p || !p.mouse) return;
      if (!dragRef.current) {
        if (Math.hypot(e.clientX - p.x, e.clientY - p.y) > SLOP / 2) startDrag(p.url);
        else return;
      }
      over(e.clientX, e.clientY);
    };
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      const url = urlAt(e.target);
      if (!url || (e.target as HTMLElement).closest("[data-art-remove]")) return;
      press.current = { url, x: e.clientX, y: e.clientY, timer: null, mouse: true };
    };

    row.addEventListener("touchstart", onTouchStart, { passive: true });
    row.addEventListener("touchmove", onTouchMove, { passive: false });
    row.addEventListener("touchend", finish);
    row.addEventListener("touchcancel", finish);
    row.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onMouseMove);
    window.addEventListener("pointerup", finish);
    return () => {
      row.removeEventListener("touchstart", onTouchStart);
      row.removeEventListener("touchmove", onTouchMove);
      row.removeEventListener("touchend", finish);
      row.removeEventListener("touchcancel", finish);
      row.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onMouseMove);
      window.removeEventListener("pointerup", finish);
    };
  }, [canManage]);

  if (images.length <= 1 && !canManage) return null;

  return (
    <div className="flex-shrink-0">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("title")}</span>
        <span className="text-xs text-muted-foreground">{t("count", { count: images.length })}</span>
      </div>
      <div
        ref={rowRef}
        className={cn("flex gap-2 overflow-x-auto hide-scrollbar pb-1 pt-1", dragUrl && "select-none")}
        style={canManage ? { WebkitTouchCallout: "none" } : undefined}
      >
        {shown.map((url, i) => {
          const dragging = dragUrl === url;
          return (
            <div
              key={url}
              data-art-idx={i}
              data-art-url={canManage ? url : undefined}
              onContextMenu={canManage ? (e) => e.preventDefault() : undefined}
              className={cn(
                "relative aspect-square w-16 h-16 sm:w-20 sm:h-20 rounded-md overflow-hidden flex-shrink-0 border-2 transition-transform duration-150",
                dragging ? "scale-110 border-primary ring-2 ring-primary/60 shadow-lg z-10" : "border-transparent hover:border-primary",
                canManage && "select-none",
              )}
            >
              <button
                type="button"
                onClick={() => { if (!suppressClick.current) onOpen(i); }}
                onKeyDown={canManage ? (e) => {
                  if (!e.altKey || (e.key !== "ArrowLeft" && e.key !== "ArrowRight")) return;
                  e.preventDefault();
                  const to = e.key === "ArrowLeft" ? i - 1 : i + 1;
                  if (to >= 0 && to < images.length) onOrder(move(images, i, to));
                } : undefined}
                className="absolute inset-0 w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                aria-label={t("open", { n: i + 1, total: images.length })}
                title={canManage ? t("dragHint") : undefined}
              >
                <Image src={url} alt="" fill sizes="80px" draggable={false} className="object-cover pointer-events-none" />
              </button>
              {canManage && !dragUrl && (
                <button
                  type="button"
                  data-art-remove
                  onClick={(e) => { e.stopPropagation(); setConfirmUrl(url); }}
                  className="absolute top-0.5 right-0.5 w-5 h-5 inline-flex items-center justify-center rounded-full bg-black/70 text-white bb-hover-reveal hover:bg-destructive text-3xs leading-none focus:opacity-100 focus:outline-none focus:bg-destructive"
                  aria-label={t("remove")}
                >
                  ×
                </button>
              )}
              {i === 0 && (
                <span className="absolute bottom-0.5 left-0.5 text-4xs bg-black/70 text-white px-1 py-0.5 rounded font-semibold pointer-events-none">
                  {t("cover")}
                </span>
              )}
            </div>
          );
        })}
        {canManage && (
          <button
            type="button"
            onClick={onAdd}
            disabled={uploading}
            className="relative aspect-square w-16 h-16 sm:w-20 sm:h-20 rounded-md flex-shrink-0 border-2 border-dashed border-border bg-muted/40 hover:border-primary hover:bg-muted transition-colors flex flex-col items-center justify-center text-muted-foreground hover:text-primary focus:outline-none focus:border-primary disabled:opacity-50"
            aria-label={t("addMore")}
          >
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
              <>
                <Upload className="h-4 w-4" />
                <span className="text-3xs font-medium mt-0.5">{t("add")}</span>
              </>
            )}
          </button>
        )}
      </div>
      {confirmUrl && (
        <div role="alertdialog" aria-label={t("removeConfirm")} className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-border bg-card p-2 text-sm">
          <span className="relative h-10 w-10 shrink-0 overflow-hidden rounded">
            <Image src={confirmUrl} alt="" fill sizes="40px" className="object-cover" />
          </span>
          <span className="min-w-0 flex-1">{t("removeConfirm")}</span>
          <button type="button" autoFocus className="h-8 rounded-md bg-destructive px-3 text-xs font-semibold text-white hover:bg-destructive/90"
            onClick={() => { const u = confirmUrl; setConfirmUrl(null); onRemove(u); }}>{t("remove")}</button>
          <button type="button" className="h-8 rounded-md border border-border px-3 text-xs font-semibold text-foreground hover:bg-muted"
            onClick={() => setConfirmUrl(null)}>{tc("cancel")}</button>
        </div>
      )}
      {canManage && images.length > 1 && (
        <p className="text-2xs text-muted-foreground mt-1">{t("dragHint")}</p>
      )}
    </div>
  );
}
